import { useMemo, useRef } from "react";
import { CallState } from "@hopper-b2b/types";
import { calculateMaxApplicablePoints } from "../utils/calculateMaxApplicablePoints";
import { useRewardsData } from "./useRewardsData";
import type { PointsBalanceType } from "@/modules/account";

/**
 * Calculates max applicable points based on rewards data.
 * Encapsulates the business logic for determining how many points can be applied.
 */
export const useRewardsCalculation = () => {
  const {
    isRewardsLoading,
    cartUpdateCallState,
    pointsBalance,
    rewardsAccountId,
    isFixedPrice,
    baseFareFixedRewardsPrice,
    pointsRedemptionMaximum,
  } = useRewardsData();

  // While a quote update is in flight, keep the last computed value instead
  // of returning undefined, which would unmount the rewards section.
  const lastComputedRef = useRef<
    Omit<PointsBalanceType, "basePointsToCashMultiplier"> | undefined
  >(undefined);

  const maxApplicablePoints:
    | Omit<PointsBalanceType, "basePointsToCashMultiplier">
    | undefined = useMemo(() => {
    if (isRewardsLoading || cartUpdateCallState === CallState.InProcess) {
      return lastComputedRef.current;
    }

    lastComputedRef.current = calculateMaxApplicablePoints({
      pointsBalance,
      rewardsAccountId,
      isFixedPrice,
      baseFareFixedRewardsPrice,
      pointsRedemptionMaximum,
    });
    return lastComputedRef.current;
  }, [
    isRewardsLoading,
    cartUpdateCallState,
    pointsBalance,
    rewardsAccountId,
    isFixedPrice,
    baseFareFixedRewardsPrice,
    pointsRedemptionMaximum,
  ]);

  return {
    maxApplicablePoints,
  };
};
